// ============================================================
// ATRIBUCION
// ------------------------------------------------------------
// Problema: el usuario llega desde un anuncio con ?utm_campaign=...
// pero si navega a otra pagina del sitio (logo, footer, legales)
// esos parametros se pierden de la URL. Cuando vuelve y hace
// click al CTA, ya no sabemos que anuncio lo trajo.
//
// Solucion: capturamos los parametros la PRIMERA vez que llega y
// los guardamos en sessionStorage. A partir de ahi el subId es
// correcto aunque navegue por todo el sitio.
//
// sessionStorage (no localStorage) a proposito: la atribucion
// muere cuando cierra la pestana, que es lo correcto.
// ============================================================

const KEY = 'blinkblis_attribution'

// Los parametros que nos interesan, en orden de prioridad para el tag
const PARAMS = [
  'utm_campaign',
  'utm_content',
  'campaign_id',
  'adset_id',
  'ad_id',
  'utm_source',
  'utm_medium',
  'src',
] as const

export type Attribution = Partial<Record<(typeof PARAMS)[number], string>>

function read(): Attribution {
  try {
    const raw = sessionStorage.getItem(KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function save(data: Attribution) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(data))
  } catch {
    // modo incognito o storage bloqueado: seguimos sin guardar
  }
}

/**
 * Lee los parametros de la URL actual. Si encuentra alguno, lo guarda
 * para el resto de la sesion. Si no encuentra ninguno, devuelve lo que
 * se haya guardado antes.
 */
export function captureAttribution(): Attribution {
  let fromUrl: Attribution = {}
  try {
    const q = new URLSearchParams(window.location.search)
    PARAMS.forEach((k) => {
      const v = q.get(k)
      if (v) fromUrl[k] = v.slice(0, 80)
    })
  } catch {
    // sin window
  }

  if (Object.keys(fromUrl).length > 0) {
    const merged = { ...read(), ...fromUrl }
    save(merged)
    return merged
  }

  return read()
}

/**
 * Construye la etiqueta que viaja a impact como subId1.
 * Prefiere los IDs numericos de Meta porque son estables;
 * si no hay, cae al nombre de campana o a la fuente.
 */
export function attributionTag(slug: string): string {
  const a = captureAttribution()
  const parts = [
    a.campaign_id || a.utm_campaign,
    a.ad_id || a.utm_content,
    a.adset_id,
    a.src,
  ].filter(Boolean)

  const base = `blinkblis_${slug}`
  if (!parts.length) return base
  return `${base}_${parts.join('-')}`
    .replace(/[^a-zA-Z0-9_\-]/g, '')
    .slice(0, 120)
}
